import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { userAPI, limitsAPI } from '../services/api';
import { responseData } from '../services/apiResponse';
import { AlertCircle, ArrowLeft, CheckCircle2, DollarSign, Info, Loader2, Save } from 'lucide-react';
import PortalHeader from '../components/PortalHeader';

const LimitSettings = () => {
  const navigate = useNavigate();
  const [yearlyLimit, setYearlyLimit] = useState('9999');
  const [monthlyLimit, setMonthlyLimit] = useState('3000');
  const [transferLimits, setTransferLimits] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    loadUserInfo();
    loadTransferLimits();
  }, []);

  const loadUserInfo = async () => {
    try {
      setLoading(true);
      const userId = localStorage.getItem('user_id');
      if (!userId) {
        setError('Please login first');
        return;
      }

      const response = await userAPI.getCurrentUser(userId);
      const user = responseData(response);
      setYearlyLimit(user.yearly_limit_usd.toString());
      setMonthlyLimit(user.monthly_limit_usd.toString());
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load user info');
    } finally {
      setLoading(false);
    }
  };

  const loadTransferLimits = async () => {
    try {
      const response = await limitsAPI.getLimits();
      setTransferLimits(responseData(response) || null);
    } catch (err) {
      console.error('Failed to load transfer limits:', err);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      // Validate against global transfer limits
      if (transferLimits) {
        const perMonthLimit = parseFloat(transferLimits.per_month) || 2999;
        const perYearLimit = parseFloat(transferLimits.per_year) || 9999;

        if (parseFloat(yearlyLimit) > perYearLimit) {
          setError(`Yearly limit cannot exceed the per year limit of $${perYearLimit.toFixed(2)}`);
          return;
        }

        if (parseFloat(monthlyLimit) > perMonthLimit) {
          setError(`Monthly limit cannot exceed the monthly limit of $${perMonthLimit.toFixed(2)}`);
          return;
        }
      }

      const userId = localStorage.getItem('user_id');
      await userAPI.updateLimits(userId, {
        yearly_limit_usd: parseFloat(yearlyLimit),
        monthly_limit_usd: parseFloat(monthlyLimit)
      });

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      
      // 重新加载用户信息
      await loadUserInfo();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update limits');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 text-slate-900">
        <PortalHeader showHome />
        <main className="flex min-h-64 items-center justify-center">
          <Loader2 className="h-7 w-7 animate-spin text-cyan-700" aria-label="Loading transaction limits" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <PortalHeader showHome />
      <main className="mx-auto max-w-3xl px-0 py-5 sm:px-4 sm:py-8">
        <div className="space-y-6 border-y border-slate-200 bg-white px-4 py-5 sm:border-x sm:px-6 sm:py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                aria-label="Back"
                className="rounded p-2 transition-colors hover:bg-slate-100"
              >
                <ArrowLeft className="h-5 w-5 text-slate-600" aria-hidden="true" />
              </button>
              <div>
                <h1 className="text-lg font-semibold text-slate-900">Transaction Limits</h1>
                <p className="mt-1 text-sm text-slate-600">Manage your yearly and monthly spending limits</p>
              </div>
            </div>
          </div>

          {error && (
            <div className="flex items-start space-x-3 rounded border border-red-200 bg-red-50 p-4" role="status" aria-live="polite">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-red-800 text-sm">{error}</p>
            </div>
          )}

          {success && (
            <div className="flex items-center gap-2 rounded border border-emerald-200 bg-emerald-50 p-4" role="status" aria-live="polite">
              <CheckCircle2 className="h-4 w-4 text-emerald-700" aria-hidden="true" />
              <p className="text-sm font-medium text-emerald-800">Limits updated successfully.</p>
            </div>
          )}

          {transferLimits && (
            <div className="space-y-3 rounded border border-slate-200 bg-slate-50 p-4">
              <h3 className="font-semibold text-gray-800 text-sm flex items-center gap-2">
                <Info className="w-4 h-4" />
                Transaction Limits
              </h3>
              <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
                <div className="rounded border border-slate-200 bg-white p-3">
                  <p className="text-gray-500 text-xs mb-1">Per Transaction</p>
                  <p className="font-bold text-lg text-gray-900">${parseFloat(transferLimits.per_transaction).toFixed(2)}</p>
                </div>
                <div className="rounded border border-slate-200 bg-white p-3">
                  <p className="text-gray-500 text-xs mb-1">Per Month</p>
                  <p className="font-bold text-lg text-gray-900">${parseFloat(transferLimits.per_month).toFixed(2)}</p>
                </div>
                <div className="rounded border border-slate-200 bg-white p-3">
                  <p className="text-gray-500 text-xs mb-1">Per Year</p>
                  <p className="font-bold text-lg text-gray-900">${parseFloat(transferLimits.per_year).toFixed(2)}</p>
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Maximum limits allowed by the system. Your personal limits cannot exceed these values.
              </p>
            </div>
          )}

          <form onSubmit={handleSave} className="space-y-6">
            <div>
              <label htmlFor="yearly-limit" className="block text-sm font-semibold text-gray-700 mb-2">
                <DollarSign className="inline w-4 h-4 mr-1" />
                Yearly Limit (USD) *
              </label>
              <input
                id="yearly-limit"
                name="yearly_limit"
                type="number"
                step="0.01"
                min="1"
                value={yearlyLimit}
                onChange={(e) => setYearlyLimit(e.target.value)}
                required
                className="input-field text-lg font-semibold"
                placeholder="9999.00"
              />
              <p className="text-xs text-gray-500 mt-1">
                Maximum amount you can send per year
              </p>
            </div>

            <div>
              <label htmlFor="monthly-limit" className="block text-sm font-semibold text-gray-700 mb-2">
                <DollarSign className="inline w-4 h-4 mr-1" />
                Monthly Limit (USD) *
              </label>
              <input
                id="monthly-limit"
                name="monthly_limit"
                type="number"
                step="0.01"
                min="1"
                value={monthlyLimit}
                onChange={(e) => setMonthlyLimit(e.target.value)}
                required
                className="input-field text-lg font-semibold"
                placeholder="3000.00"
              />
              <p className="text-xs text-gray-500 mt-1">
                Maximum amount you can send per month
              </p>
            </div>

            <div className="rounded border border-amber-200 bg-amber-50 p-4">
              <h3 className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-900">
                <AlertCircle className="h-4 w-4" aria-hidden="true" />
                Important Notes
              </h3>
              <ul className="ml-5 list-disc space-y-1 text-xs text-amber-900">
                <li>Yearly limits reset every 365 days</li>
                <li>Monthly limits reset every 30 days</li>
                <li>Yearly limit should be greater than or equal to monthly limit</li>
                <li>Changes take effect immediately</li>
              </ul>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="btn-primary flex flex-1 items-center justify-center space-x-2"
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="w-5 h-5" />
                    <span>Save Changes</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default LimitSettings;
